import type { QueryClient } from '@tanstack/react-query';
import { nanoid } from 'nanoid';

import type { localStorage } from '@change-corgi/core/storage';

import type { DraftAuthorIdAdapter } from './types';

function createLocalStorageDraftAuthorIdAdapter(storage: typeof localStorage): DraftAuthorIdAdapter {
	const key = 'draft-author-id';
	const legacyKey = 'draft-id';
	return {
		get: () => storage.getItem(key),
		set: (id) => {
			storage.setItem(key, id);
		},
		findOrCreate: () => {
			const existing = storage.getItem(key);
			if (existing) return existing;

			// migrate from legacy key if present
			const legacy = storage.getItem(legacyKey);
			if (legacy) {
				storage.setItem(key, legacy);
				storage.removeItem(legacyKey);
				return legacy;
			}

			const newId = nanoid();
			storage.setItem(key, newId);
			return newId;
		},
		delete: () => {
			const id = storage.getItem(key);
			storage.removeItem(key);
			storage.removeItem(legacyKey);
			// cleanup any drafts associated with this authorId in localStorage
			// to prevent orphaned drafts from accumulating in memory during the session
			if (id) storage.removeItem(`draft-${id}`);
		},
	};
}

function createInMemoryDraftAuthorIdAdapter(queryClient: QueryClient): DraftAuthorIdAdapter {
	const queryKey = ['draft-author-id'] as const;
	return {
		get: () => queryClient.getQueryData<string>(queryKey) ?? null,
		set: (id) => {
			queryClient.setQueryData(queryKey, id);
		},
		findOrCreate: () => {
			const existing = queryClient.getQueryData<string>(queryKey);
			if (existing) return existing;
			const newId = nanoid();
			queryClient.setQueryData(queryKey, newId);
			return newId;
		},
		delete: () => {
			const id = queryClient.getQueryData<string>(queryKey);
			queryClient.removeQueries({ queryKey });
			// cleanup any drafts associated with this authorId in memory
			// to prevent orphaned drafts from accumulating in memory during the session
			if (id) queryClient.removeQueries({ queryKey: ['draft', id] });
		},
	};
}

// TODO: move draft id logic to server cookie and replace with graphql fragment?
type FindOrCreateDraftAuthorIdParams = { storage: typeof localStorage; queryClient: QueryClient };
export function findOrCreateDraftAuthorId({ storage, queryClient }: FindOrCreateDraftAuthorIdParams): string {
	const adapters: DraftAuthorIdAdapter[] = [
		createLocalStorageDraftAuthorIdAdapter(storage),
		createInMemoryDraftAuthorIdAdapter(queryClient),
	];

	const errors: Error[] = [];
	for (const adapter of adapters) {
		try {
			return adapter.findOrCreate();
		} catch (error) {
			errors.push(error instanceof Error ? error : new Error(String(error)));
		}
	}

	throw new Error(`All adapters failed: ${errors.map((e) => e.message).join(' | ')}`);
}

type DeleteDraftAuthorParams = { storage: typeof localStorage; queryClient: QueryClient };
export function deleteDraftAuthor({ storage, queryClient }: DeleteDraftAuthorParams): void {
	const adapters: DraftAuthorIdAdapter[] = [
		createLocalStorageDraftAuthorIdAdapter(storage),
		createInMemoryDraftAuthorIdAdapter(queryClient),
	];

	const errors: Error[] = [];
	for (const adapter of adapters) {
		try {
			adapter.delete();
		} catch (error) {
			errors.push(error instanceof Error ? error : new Error(String(error)));
		}
	}

	if (errors.length > 0) {
		throw new Error(`Some adapters failed to delete: ${errors.map((e) => e.message).join(' | ')}`);
	}
}
